import { trackReportEvent } from '../report-tracking'

import type { HeapProperties } from '@app/adapters/heap'
import type { ColumnType } from './types'

// Named: Heap creates an event on first sight, so a typo starts a seventh one.
type InlineColumnsEvent =
  | 'Opened'
  | 'Column Edited'
  | 'Applied'
  | 'Apply Failed'
  | 'Column Applied'
  | 'Abandoned'

// A prefix naming this editor, not the columns: Report Settings edits the same
// columns, and the two surfaces are being compared against each other.
export function trackInlineColumns(
  action: InlineColumnsEvent,
  properties: HeapProperties = {},
  report?: HeapProperties
): void {
  trackReportEvent(`Inline Columns: ${action}`, properties, report)
}

// Round-tripped so an apply keeps them; `formula` is user free text.
const UNTRACKED_FIELDS = ['formula', 'formula_title']

export const isTrackedField = (key: string): boolean =>
  !UNTRACKED_FIELDS.includes(key)

// What the help centre calls each of a column's dropdowns ("calculation method",
// "data source", "variance"), rather than the attribute names the model carries.
// The model's names read as jargon in an analytics tool, and `delta` and
// `periods` mean a different thing on a difference column - operand A's - than
// they do on a period column, which no chart would show.
//
// Keyed by column type for that reason. An unmapped key travels as itself.
const FIELD_NAMES: Record<ColumnType, Record<string, string>> = {
  Period: {
    relative: 'calculation',
    show: 'data_source',
    periods: 'period',
    delta: 'year',
  },
  PeriodAnalytical: {
    relative: 'calculation',
    show: 'data_source',
    periods: 'period',
    delta: 'year',
    analytical_type: 'analytical_type',
  },
  PeriodDifference: {
    show_as: 'variance',
    relative: 'calculation',
    periods_value_type: 'data_source_a',
    periods: 'period_a',
    delta: 'year_a',
    periods_2_value_type: 'data_source_b',
    periods_2: 'period_b',
    delta_2: 'year_b',
  },
  ColumnSpacer: {},
}

export const fieldName = (type: ColumnType, key: string): string =>
  FIELD_NAMES[type][key] ?? key

// A budget-set value carries its id (`budget-42`), which would be one value per
// set per firm. Dropped to the shape the server parses; `.+` keeps `delta` -1.
export const fieldValue = <T>(value: T): T | string =>
  typeof value === 'string' ? value.replace(/^(.+)-\d+$/, '$1') : value
